import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Box, Typography, TextField, IconButton, Avatar, InputAdornment, Select, MenuItem, Menu } from "@mui/material";
import Table from "react-bootstrap/Table";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import { encryptId } from "../helper/idCodec.js";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../LoadingContext";
import "./Drivers.css";

const getInitials = (name) => {
    const parts = name.split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    if (parts.length > 1) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    return "";
};

const OnDutyTab = () => {
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchTrigger, setSearchTrigger] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [usersPerPage, setUsersPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const spinnerRef = useRef(true);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
    const openUserMenu = Boolean(userMenuAnchorEl);
    const navigate = useNavigate();

    // Fetch onDuty drivers
    useEffect(() => {
        let minSpinnerTimeout;
        let apiRequestCompleted = false;

        const fetchDrivers = async () => {
            setLoading(true);
            spinnerRef.current = true;
            setError(null);

            // Minimum 3 seconds spinner
            minSpinnerTimeout = setTimeout(() => {
                spinnerRef.current = false;
                if (apiRequestCompleted) setLoading(false);
            }, 3000);

            try {
                const params = { page: currentPage + 1, limit: usersPerPage, isOnDuty: true };
                if (searchTrigger) params.search = searchTrigger;

                const response = await getData(API_ENDPOINTS.getDriversList, params);

                const fetchedUsers = response.result.map((driver) => ({
                    id: driver.driver_id,
                    name: `${driver.first_name} ${driver.last_name}`,
                    email: driver.email,
                    avatar: driver.pic || undefined,
                    isOnDuty: driver.isOnDuty,
                    mobile_num: driver.mobile_num,
                    driverId: String(driver.driver_id),
                    licenseNo: String(driver.license_no),
                    rating: driver.rating ?? 0
                }));

                setUsers(fetchedUsers);
            } catch (err) {
                console.error("Failed to fetch drivers:", err);
                setError(err.message || "Failed to fetch drivers.");
            } finally {
                apiRequestCompleted = true;
                if (!spinnerRef.current) setLoading(false);
            }
        };

        fetchDrivers();

        return () => clearTimeout(minSpinnerTimeout);
    }, [currentPage, usersPerPage, searchTrigger]);

    const filteredUsers = useMemo(() => users, [users]);
    const paginatedUsers = useMemo(() => {
        const startIndex = currentPage * usersPerPage;
        return filteredUsers.slice(startIndex, startIndex + usersPerPage);
    }, [filteredUsers, currentPage, usersPerPage]);

    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    // Pagination handlers
    const handlePageChange = (event, newPage) => {
        if (newPage >= 0 && newPage < totalPages) setCurrentPage(newPage);
    };

    const handleUsersPerPageChange = (event) => {
        setUsersPerPage(event.target.value);
        setCurrentPage(0);
    };

    // User menu
    const handleMoreOptionsClick = (event, userId) => {
        setUserMenuAnchorEl(event.currentTarget);
        setSelectedUserId(userId);
    };

    const handleUserMenuClose = () => { setUserMenuAnchorEl(null); setSelectedUserId(null); };

    // View Profile
    const handleViewProfile = useCallback((userId) => {
        const driverId = encryptId(userId);
        navigate(`/app/driver-details/${driverId}`);
        handleUserMenuClose();
    }, [navigate]);

    // Search handlers
    const handleSearchChange = (e) => setSearchTerm(e.target.value);
    const triggerSearch = useCallback(() => setSearchTrigger(searchTerm), [searchTerm]);
    const handleSearchKeyDown = (e) => { if (e.key === "Enter") triggerSearch(); };
    const handleClearSearch = () => { setSearchTerm(""); setSearchTrigger(""); setCurrentPage(0); };

    const renderStatus = (isOnDuty) => (
        <span className={`status-indicator status-${isOnDuty ? "on-duty" : "off-duty"}`}>
            <span className="status-dot"></span> {isOnDuty ? "On Duty" : "Off Duty"}
        </span>
    );

    return (
        <Box>
            <LoadingSpinner isLoading={loading} />

            {/* Search */}
            <Row className="control-bar mb-4 align-items-center">
                <Col xs={12} md={12} className="search-col d-flex align-items-center">
                    <TextField
                        fullWidth
                        placeholder="Search Driver by name or ID"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleClearSearch} size="small"><ClearIcon /></IconButton>
                                </InputAdornment>
                            ),
                            sx: { borderRadius: "8px" },
                        }}
                    />
                </Col>
            </Row>

            {error && <Typography color="error">{error}</Typography>}
            {!loading && paginatedUsers.length === 0 && (
                <Typography variant="body1" color="textSecondary" className="text-center mt-4">
                    No drivers found.
                </Typography>
            )}

            {/* Table */}
            {!loading && paginatedUsers.length > 0 && (
                <div className="list-view-container">
                    <Table responsive hover className="user-list-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Driver ID</th>
                                <th>License No.</th>
                                <th>Status</th>
                                <th>Mobile Number</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedUsers.map((user) => (
                                <tr key={user.id}>
                                    <td>
                                        <div className="user-name-cell" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <Avatar src={user.avatar} alt={user.name} sx={{ width: 28, height: 28 }}>
                                                {!user.avatar && getInitials(user.name)}
                                            </Avatar>
                                            <span>{user.name}</span>
                                        </div>
                                    </td>
                                    <td>{user.driverId}</td>
                                    <td>{user.licenseNo}</td>
                                    <td>{renderStatus(user.isOnDuty)}</td>
                                    <td>{user.mobile_num}</td>
                                    <td className="text-end">
                                        <IconButton size="small" onClick={(e) => handleMoreOptionsClick(e, user.id)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu anchorEl={userMenuAnchorEl} open={openUserMenu && selectedUserId === user.id} onClose={handleUserMenuClose}>
                                            <MenuItem onClick={() => handleViewProfile(selectedUserId)}>
                                                View Profile
                                            </MenuItem>
                                        </Menu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Pagination */}
            {!loading && filteredUsers.length > 0 && (
                <Box className="pagination-controls" display="flex" justifyContent="end" alignItems="center" mt={2}>
                    <div className="items-per-page" style={{ display: "flex", alignItems: "center" }}>
                        <Select value={usersPerPage} onChange={handleUsersPerPageChange} size="small">
                            {[5, 10, 25, 50].map((opt) => (<MenuItem key={opt} value={opt}>{opt}</MenuItem>))}
                        </Select>
                        <Typography variant="body2" sx={{ ml: 1 }}>per page</Typography>
                    </div>

                    <Typography variant="body2" sx={{ mx: 2 }}>
                        {currentPage + 1} of {totalPages} pages
                    </Typography>

                    <IconButton onClick={() => handlePageChange(null, currentPage - 1)} disabled={currentPage === 0}>
                        <KeyboardArrowLeft />
                    </IconButton>
                    <IconButton onClick={() => handlePageChange(null, currentPage + 1)} disabled={currentPage >= totalPages - 1}>
                        <KeyboardArrowRight />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
};

export default OnDutyTab;
